package com.milta.feature.route.presentation

import com.milta.core.appconfig.AppConfigRepository
import com.milta.core.cache.LocalCacheStore
import com.milta.core.mvi.MviViewModel
import com.milta.core.network.AiCreditExhaustedException
import com.milta.core.network.aiCreditResetAt
import com.milta.core.network.ApiException
import com.milta.feature.aicredit.presentation.AiCreditStore
import com.milta.core.network.isOptimizationQuotaExceeded
import com.milta.feature.optimizationquota.presentation.OptimizationQuotaStore
import com.milta.feature.optimizationquota.presentation.quotaResetAt
import com.milta.feature.route.domain.usecase.ReoptimizeRouteBatchUseCase
import com.milta.core.observability.ErrorReporting.isConnectivityFailure
import com.milta.feature.delivery.domain.usecase.ValidateAddressUseCase
import com.milta.feature.importjobs.domain.entity.ImportJob
import com.milta.feature.importjobs.domain.entity.ImportJobErrorCodes
import com.milta.feature.importjobs.domain.entity.ImportJobFlow
import com.milta.feature.importjobs.domain.entity.ImportJobResult
import com.milta.feature.importjobs.domain.entity.ImportJobStatus
import com.milta.feature.importjobs.presentation.ImportJobFailureKind
import com.milta.feature.importjobs.presentation.ImportJobTracker
import com.milta.feature.importjobs.presentation.failureKind
import com.milta.feature.importjobs.presentation.isOriginRouteDeleted
import com.milta.feature.importjobs.presentation.offersServerRetry
import com.milta.feature.importjobs.presentation.realProgress
import com.milta.feature.importjobs.presentation.startedAtEpochMillis
import com.milta.feature.imports.domain.entity.ImportPdfResult
import com.milta.feature.imports.domain.usecase.ExtractDeliveriesFromPdfUseCase
import com.milta.feature.route.domain.entity.AddressesToFixException
import com.milta.feature.route.domain.entity.Delivery
import com.milta.feature.route.domain.entity.GeocodeStatus
import com.milta.feature.route.domain.entity.addressFixReason
import com.milta.feature.route.domain.entity.markAddressesToFix
import com.milta.feature.route.domain.usecase.DeleteRouteBatchUseCase
import com.milta.feature.delivery.domain.entity.AddressValidation
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import com.milta.feature.delivery.domain.entity.AddressSuggestion
import com.milta.feature.route.domain.entity.RouteBatchResult
import com.milta.feature.route.domain.usecase.AddLoadingAddressUseCase
import com.milta.feature.route.domain.usecase.CreateRouteBatchUseCase
import com.milta.feature.route.domain.usecase.DeleteLoadingAddressUseCase
import com.milta.feature.route.domain.usecase.GetLoadingAddressesUseCase
import com.milta.feature.route.domain.usecase.DispatchRouteBatchUseCase
import com.milta.feature.route.domain.usecase.GetRouteDetailUseCase
import com.milta.feature.route.domain.usecase.UpdateDeliveryAddressUseCase
import com.milta.feature.spreadsheetimport.domain.entity.SpreadsheetImportResult
import com.milta.feature.spreadsheetimport.domain.usecase.ImportSpreadsheetUseCase
import com.milta.feature.team.domain.usecase.GetEnterpriseDriversUseCase
import com.milta.feature.team.domain.usecase.GetMyTeamUseCase
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json

internal const val REVALIDATION_DEBOUNCE_MS = 700L
private const val DRAFT_STORAGE_KEY = "web_import_route_draft"
private val draftJson = Json { ignoreUnknownKeys = true }

private const val HTTP_BAD_REQUEST = 400
private const val HTTP_UNPROCESSABLE_ENTITY = 422
private const val HTTP_FORBIDDEN = 403
private const val HTTP_NOT_FOUND = 404
private const val HTTP_CONFLICT = 409
private const val HTTP_SERVICE_UNAVAILABLE = 503

/**
 * ViewModel for the web dashboard's "Ajouter une tournée" flow (MIL-50/MIL-51, mockup
 * `docs/mockups/MIL-50.html`) — a three-step wizard (Écran 1 "Importer" / Écran 2 "Vérifier &
 * assigner" / Écran 3 "Valider et envoyer les tournées") driving all three screens off a single
 * [WebImportRouteState.phase]. Reuses the same use cases already wired for the mobile Gestionnaire
 * flow ([CreateRouteBatchUseCase]/[DispatchRouteBatchUseCase]/[GetRouteDetailUseCase]/
 * [GetMyTeamUseCase]/[GetEnterpriseDriversUseCase], see `CreateRouteScreen` (multi-driver selection
 * merged into it)/`RouteBatchPreviewScreen`) rather than duplicating them — only [ImportSpreadsheetUseCase] and the
 * wizard state itself are new.
 *
 * Note (MIL-50 scope update, 2026-08-20): PDF is now accepted too, routed to the same
 * [ExtractDeliveriesFromPdfUseCase]/`POST /imports/pdf` already wired for mobile (see
 * `ImportPdfValidationViewModel`) rather than a new extraction service — `.pdf` files branch off
 * [importFile] before reaching [importSpreadsheet], everything downstream (rows, Écran 2) is
 * unchanged since both paths map to the same [WebImportRouteRow].
 *
 * Note (MIL-51, multi-driver): Écran 2's submit now always goes through `POST /route-batches`
 * (MIL-39 contract) — even for a single selected driver, no N=1 special case — and no longer
 * dispatches immediately: a successful create moves to Écran 3 ([WebImportRoutePhase.BatchResult])
 * so the manager reviews the split before "Valider et envoyer les tournées" fires the actual
 * `POST /route-batches/{id}/dispatch`. The single-route `CreateRouteUseCase`/`DispatchRouteUseCase`
 * pair used by this ViewModel before MIL-51 is gone — see `CreateRouteViewModel`/`DispatchViewModel`
 * for where that pair still lives, unrelated to this web wizard.
 *
 * Note (RoutaR-unavailable fallback): when [submit] fails with `optimization_unavailable`, Écran 2d
 * offers a second action ([WebImportRouteIntent.SubmitWithoutOptimization]) that retries the same
 * `POST /route-batches` with `optimize = false` — the round-robin split already implemented for
 * MIL-39, just never wired to the web wizard before. Not an automatic fallback: the manager decides.
 *
 * Note (asynchronous imports, maquette `docs/mockups/async-imports-v2.html`, web): the file is now
 * uploaded to `/import-jobs` through the app-wide [ImportJobTracker] instead of the synchronous
 * import calls above — the manager may leave the page while it is analysed, and finds the import
 * again on return. "Réessayer" relaunches it server-side without re-sending the file. The import
 * is acknowledged only once its deliveries exist (batch created) or it is explicitly given up.
 */
class WebImportRouteViewModel(
    private val importJobTracker: ImportJobTracker,
    private val getMyTeam: GetMyTeamUseCase,
    private val getEnterpriseDrivers: GetEnterpriseDriversUseCase,
    private val createRouteBatch: CreateRouteBatchUseCase,
    private val dispatchRouteBatch: DispatchRouteBatchUseCase,
    private val getRouteDetail: GetRouteDetailUseCase,
    private val appConfigRepository: AppConfigRepository,
    private val localCacheStore: LocalCacheStore,
    private val validateAddress: ValidateAddressUseCase,
    // MIL-99 — "Adresse de chargement" card of Écran 2.
    private val getLoadingAddresses: GetLoadingAddressesUseCase,
    private val addLoadingAddress: AddLoadingAddressUseCase,
    private val deleteLoadingAddress: DeleteLoadingAddressUseCase,
    // MIL-99 — "Corriger l'adresse" on Écran 3. Nullable with a null default so the existing call
    // sites (tests) keep compiling unchanged; WebImportRouteScreen always passes it.
    private val updateDeliveryAddress: UpdateDeliveryAddressUseCase? = null,
    // MIL-97 — "Ré-optimiser" on Écran 3 (E2) and the Enterprise's own counter, refreshed after
    // each optimization (rule 5). Nullable for the same reason as updateDeliveryAddress.
    private val reoptimizeRouteBatch: ReoptimizeRouteBatchUseCase? = null,
    private val optimizationQuotaStore: OptimizationQuotaStore? = null,
    // MIL-98 — the Enterprise's AI credit: a PDF is refused here at 0 (R4), re-read after each
    // import. Nullable for the same reason as updateDeliveryAddress.
    private val aiCreditStore: AiCreditStore? = null,
    // Retour 26/10 (R8) — abandons the split not sent yet before a recompute. Nullable for the same
    // reason as updateDeliveryAddress: the new split is then simply created.
    private val deleteRouteBatch: DeleteRouteBatchUseCase? = null,
    // Runs the HERE re-validation debounce (scheduleRevalidation). Tests pass the runTest
    // scheduler's dispatcher so the 700 ms debounce runs in virtual time instead of on the real clock.
    private val revalidationDispatcher: CoroutineDispatcher = Dispatchers.Default,
    // Runs the ViewModel's coroutines (see MviViewModel): tests pass the runTest scheduler's dispatcher.
    dispatcher: CoroutineDispatcher = Dispatchers.Default,
) : MviViewModel<WebImportRouteState, WebImportRouteIntent>(WebImportRouteState(), dispatcher) {

    private val _navEvent = Channel<WebImportRouteNavEvent>(Channel.BUFFERED)
    val navEvent = _navEvent.receiveAsFlow()

    /** Local file kept only until it reached the server — re-sent by "Réessayer" after an upload failure. */
    private var pendingFile: Pair<ByteArray, String>? = null

    /** Import currently followed on Écran 1 (running or failed), null once it is shown on Écran 2. */
    private var followedJobId: String? = null

    /** Import whose deliveries are on Écran 2/3 — acknowledged once its routes are created. */
    private var shownJobId: String? = null

    /** Finished import kept by an error screen: acknowledged before the next file is sent. */
    private var abandonableJobId: String? = null

    /** Retour 26/10 (R9) — import of a file ADDED to the list already shown (not a new list). */
    private var appendJobId: String? = null

    init {
        // Ad hoc feature (2026-08) — RoutaR kill switch: known ahead of time so Écran 2 never even
        // shows/attempts the "Optimiser l'itinéraire" CTA when it would fail every time.
        scope.launch {
            val routarEnabled = appConfigRepository.isRoutarEnabled()
            setState { copy(routarEnabled = routarEnabled) }
        }
        restoreDraftIfAny()
        refreshQuota()
        refreshAiCredit()
        // Maquette async-imports-v2, web : back on "Ajouter une tournée", the page finds the
        // account's import again (the tracker re-reads `?active=true`) — still analysing, failed,
        // or ready (straight to Écran 2).
        scope.launch {
            importJobTracker.state.map { it.current }.distinctUntilChanged().collect { job -> onActiveJob(job) }
        }
    }

    /** Retour client — [WebImportRouteContent]'s own ViewModel is discarded every time the manager
     * leaves `/dashboard/import` (switches dashboard tab, or a real page reload/reopen), which used
     * to silently throw away an already-paid-for OCR/AI scan. A saved draft restores straight into
     * [WebImportRoutePhase.VerifyAssign] — never re-running the import call. */
    private fun restoreDraftIfAny() {
        val saved = localCacheStore.getString(DRAFT_STORAGE_KEY) ?: return
        val draft = runCatching { draftJson.decodeFromString<ImportRouteDraft>(saved) }.getOrNull() ?: return
        shownJobId = draft.jobId
        setState {
            copy(
                phase = WebImportRoutePhase.VerifyAssign,
                fileName = draft.fileName,
                totalCount = draft.rows.size,
                isStructuredFormat = true,
                rows = draft.rows,
                selectedDriverIds = draft.selectedDriverIds,
                staleBatchId = draft.batchId,
            )
        }
        loadDrivers()
        loadLoadingAddresses()
    }

    private fun persistDraft() {
        val draft = ImportRouteDraft(
            fileName = currentState.fileName,
            rows = currentState.rows,
            selectedDriverIds = currentState.selectedDriverIds,
            jobId = shownJobId,
            batchId = currentState.batchResult?.batchId ?: currentState.staleBatchId,
        )
        localCacheStore.putString(DRAFT_STORAGE_KEY, draftJson.encodeToString(draft))
    }

    /** Écran 2 — "Annuler l'import" (confirmé côté écran avant d'émettre cet intent) : abandon
     * explicite, contrairement à [handleImportFailure]/un simple changement d'onglet qui doivent
     * laisser le brouillon intact pour [restoreDraftIfAny]. The import is acknowledged too, so it
     * leaves the active list and a new file is accepted. */
    private fun discardDraft() {
        localCacheStore.remove(DRAFT_STORAGE_KEY)
        shownJobId?.let { importJobTracker.markConsumed(it) }
        shownJobId = null
        setState { WebImportRouteState(routarEnabled = routarEnabled, showCancelledNotice = true) }
    }

    override fun onIntent(intent: WebImportRouteIntent) {
        when (intent) {
            is WebImportRouteIntent.PickFile -> importFile(intent.fileBytes, intent.fileName)
            WebImportRouteIntent.RetryServiceCall -> retry()
            is WebImportRouteIntent.ToggleDriver -> toggleDriver(intent.driverId)
            is WebImportRouteIntent.UpdateRow -> updateRow(intent.rowId, intent.row)
            WebImportRouteIntent.Submit -> submit(optimize = true)
            WebImportRouteIntent.SubmitWithoutOptimization -> submit(optimize = false)
            WebImportRouteIntent.ValidateAndSend -> validateAndSend()
            WebImportRouteIntent.ClearSubmitError -> setState { copy(submitError = null) }
            WebImportRouteIntent.DiscardDraft -> discardDraft()
            is WebImportRouteIntent.SelectLoadingAddress -> setState {
                copy(selectedLoadingAddressId = intent.id, submitError = submitError.takeUnless { it == "loading_address_required" })
            }
            is WebImportRouteIntent.AddLoadingAddress -> saveLoadingAddress(intent.suggestion)
            is WebImportRouteIntent.RequestDeleteLoadingAddress -> setState { copy(loadingAddressPendingDeletion = intent.address) }
            WebImportRouteIntent.CancelDeleteLoadingAddress -> setState { copy(loadingAddressPendingDeletion = null) }
            WebImportRouteIntent.ConfirmDeleteLoadingAddress -> confirmDeleteLoadingAddress()
            is WebImportRouteIntent.FixBatchStopAddress -> fixBatchStopAddress(intent.routeId, intent.deliveryId, intent.suggestion)
            WebImportRouteIntent.ReoptimizeBatch -> reoptimizeBatch()
            WebImportRouteIntent.ShowImportStep -> if (currentState.rows.isNotEmpty()) setState { copy(phase = WebImportRoutePhase.AddFile, rowEditor = null) }
            WebImportRouteIntent.ShowVerifyStep -> if (currentState.rows.isNotEmpty()) setState { copy(phase = WebImportRoutePhase.VerifyAssign) }
            is WebImportRouteIntent.SetFilter -> setState { copy(filter = intent.filter) }
            is WebImportRouteIntent.OpenRowEditor -> setState { copy(rowEditor = WebRowEditor(intent.rowId)) }
            WebImportRouteIntent.CloseRowEditor -> setState { copy(rowEditor = null) }
            is WebImportRouteIntent.SaveRowEditor -> saveRowEditor(intent.row)
            is WebImportRouteIntent.RemoveRow -> removeRow(intent.rowId)
            WebImportRouteIntent.DismissAddedFileFailure -> setState { copy(addedFileFailure = null) }
        }
    }

    private fun nextRowId(): Int = (currentState.rows.maxOfOrNull { it.id } ?: -1) + 1

    /** R2 — removing a row is the other way out of an address to correct. */
    private fun removeRow(rowId: Int) {
        revalidationJobs.remove(rowId)?.cancel()
        setState {
            val remaining = rows.filterNot { it.id == rowId }
            copy(rows = remaining, filter = if (remaining.none { it.needsAddressFix }) DeliveryFilter.All else filter)
        }
        persistDraft()
    }

    /**
     * R4 — "Enregistrer" of the edit / manual-entry modal: the address is checked by HERE
     * (`POST /addresses/validate`) unless it is unchanged and already located. Refused (no house
     * number, not found) → nothing saved, the modal shows why. HERE unreachable or a failed call
     * never block (R3): the row is saved with that status.
     */
    private fun saveRowEditor(row: WebImportRouteRow) {
        val editor = currentState.rowEditor ?: return
        if (editor.isSaving) return
        val address = row.address?.trim().orEmpty()
        if (address.isBlank()) return
        val existing = editor.rowId?.let { id -> currentState.rows.firstOrNull { it.id == id } }
        setState { copy(rowEditor = editor.copy(isSaving = true, refusal = null)) }
        scope.launch {
            // A "Geocoded" row without coordinates was never really validated: checked again.
            val unchanged = existing?.takeIf {
                it.address?.trim() == address && it.geocodeStatus == GeocodeStatus.Geocoded && it.latitude != null && it.longitude != null
            }
            val validation = if (unchanged != null) {
                AddressValidation(unchanged.geocodeStatus, unchanged.latitude, unchanged.longitude, resolvedAddress = unchanged.resolvedAddress)
            } else {
                try {
                    validateAddress(address)
                } catch (e: CancellationException) {
                    throw e
                } catch (e: Exception) {
                    AddressValidation(GeocodeStatus.Pending, null, null)
                }
            }
            val refusal = addressFixReason(validation.geocodeStatus, validation.geocodeFailureReason)
            if (refusal != null) {
                setState { copy(rowEditor = rowEditor?.copy(isSaving = false, refusal = refusal, refusedAddress = address)) }
                return@launch
            }
            val saved = row.copy(
                id = existing?.id ?: nextRowId(),
                address = address,
                hasMissingFields = row.recipientName.isNullOrBlank(),
                packageCount = row.packageCount.coerceAtLeast(1),
                geocodeStatus = validation.geocodeStatus,
                geocodeFailureReason = validation.geocodeFailureReason,
                resolvedAddress = validation.resolvedAddress,
                latitude = validation.latitude,
                longitude = validation.longitude,
            )
            revalidationJobs.remove(saved.id)?.cancel()
            setState {
                val updated = if (existing != null) rows.map { if (it.id == saved.id) saved else it }
                else consolidateWebImportRouteRows(rows + saved)
                copy(
                    rows = updated,
                    rowEditor = null,
                    filter = if (updated.none { it.needsAddressFix }) DeliveryFilter.All else filter,
                )
            }
            persistDraft()
        }
    }

    /** MIL-98 — re-read the AI credit on opening and after every import. */
    private fun refreshAiCredit() {
        val store = aiCreditStore ?: return
        scope.launch { store.refresh() }
    }

    /** MIL-97 — rule 5: re-read the counter after every optimization action. */
    private fun refreshQuota() {
        val store = optimizationQuotaStore ?: return
        scope.launch { store.refresh() }
    }

    /** MIL-97 — E2: re-runs RoutaR on the batch not sent yet. Disabled in advance at 0 (R10); a 403
     * meanwhile keeps the current order, "Valider et envoyer" stays available (R6, R11). */
    private fun reoptimizeBatch() {
        val useCase = reoptimizeRouteBatch ?: return
        val batchId = currentState.batchResult?.batchId ?: return
        if (currentState.isReoptimizingBatch || currentState.isDispatchingBatch) return
        if (optimizationQuotaStore?.quota?.value?.isExhausted == true) return
        setState { copy(isReoptimizingBatch = true, submitError = null) }
        scope.launch {
            runCatching { useCase(batchId) }.fold(
                onSuccess = { result ->
                    setState { copy(isReoptimizingBatch = false, batchResult = result) }
                    refreshQuota()
                    loadBatchStops(result)
                },
                onFailure = { e ->
                    setState {
                        copy(
                            isReoptimizingBatch = false,
                            submitError = mapBatchError(e),
                            quotaReachedResetAt = e.quotaResetAt ?: optimizationQuotaStore?.quota?.value?.resetAt,
                        )
                    }
                    if (e.isOptimizationQuotaExceeded) refreshQuota()
                },
            )
        }
    }

    /** MIL-99 — Écran 3, the batch is created but not sent yet: PUT the picked HERE suggestion on
     * the unlocated stop, then refresh it in place (its "à vérifier" badge disappears) and in the
     * batch's per-driver statuses (which drive the warning before sending). */
    private fun fixBatchStopAddress(routeId: String, deliveryId: String, suggestion: AddressSuggestion) {
        val useCase = updateDeliveryAddress ?: return
        if (deliveryId in currentState.fixingStopIds) return
        setState { copy(fixingStopIds = fixingStopIds + deliveryId, submitError = null) }
        scope.launch {
            runCatching { useCase(routeId, deliveryId, suggestion) }.fold(
                onSuccess = { updated ->
                    setState {
                        copy(
                            fixingStopIds = fixingStopIds - deliveryId,
                            batchStopsByRouteId = batchStopsByRouteId + (
                                routeId to batchStopsByRouteId[routeId].orEmpty().map { if (it.id == updated.id) updated else it }
                                ),
                            batchResult = batchResult?.copy(
                                drivers = batchResult.drivers.map { proposal ->
                                    if (proposal.routeId != routeId) proposal
                                    else proposal.copy(
                                        deliveries = proposal.deliveries.map {
                                            if (it.deliveryId == updated.id) it.copy(geocodeStatus = updated.geocodeStatus) else it
                                        },
                                    )
                                },
                            ),
                        )
                    }
                },
                onFailure = { e -> setState { copy(fixingStopIds = fixingStopIds - deliveryId, submitError = mapFixAddressError(e)) } },
            )
        }
    }

    private fun mapFixAddressError(e: Throwable): String = when {
        e is ApiException && e.statusCode == HTTP_FORBIDDEN -> "forbidden"
        else -> mapBatchError(e)
    }

    /** MIL-99 — loaded alongside the drivers each time Écran 2 opens; the list comes back
     * last-used first, so the first one is preselected. */
    private fun loadLoadingAddresses() {
        scope.launch {
            setState { copy(isLoadingLoadingAddresses = true, loadingAddressError = null) }
            getLoadingAddresses().fold(
                onSuccess = { addresses ->
                    setState {
                        copy(
                            isLoadingLoadingAddresses = false,
                            loadingAddresses = addresses,
                            selectedLoadingAddressId = addresses.firstOrNull()?.id,
                        )
                    }
                },
                onFailure = { e -> setState { copy(isLoadingLoadingAddresses = false, loadingAddressError = mapLoadingAddressError(e)) } },
            )
        }
    }

    /** POST with the picked HERE suggestion's coordinates; the saved address becomes the selection.
     * A 200 (address already saved) returns the existing one — it moves to the top, never duplicated. */
    private fun saveLoadingAddress(suggestion: AddressSuggestion) {
        if (currentState.isSavingLoadingAddress) return
        setState { copy(isSavingLoadingAddress = true, loadingAddressError = null) }
        scope.launch {
            addLoadingAddress(suggestion).fold(
                onSuccess = { added ->
                    setState {
                        copy(
                            isSavingLoadingAddress = false,
                            loadingAddresses = listOf(added) + loadingAddresses.filterNot { it.id == added.id },
                            selectedLoadingAddressId = added.id,
                            submitError = submitError.takeUnless { it == "loading_address_required" },
                        )
                    }
                },
                onFailure = { e -> setState { copy(isSavingLoadingAddress = false, loadingAddressError = mapLoadingAddressError(e)) } },
            )
        }
    }

    /** A 404 means the address is already gone server-side — removed from the list all the same.
     * Deleting the selected one leaves nothing selected (submit blocked until a new pick). */
    private fun confirmDeleteLoadingAddress() {
        val target = currentState.loadingAddressPendingDeletion ?: return
        setState { copy(loadingAddressPendingDeletion = null, loadingAddressError = null) }
        scope.launch {
            val error = deleteLoadingAddress(target.id).exceptionOrNull()
            if (error == null || (error is ApiException && error.statusCode == HTTP_NOT_FOUND)) {
                setState {
                    copy(
                        loadingAddresses = loadingAddresses.filterNot { it.id == target.id },
                        selectedLoadingAddressId = selectedLoadingAddressId.takeUnless { it == target.id },
                    )
                }
            } else {
                setState { copy(loadingAddressError = mapLoadingAddressError(error)) }
            }
        }
    }

    private fun mapLoadingAddressError(e: Throwable): String =
        if (e is ApiException) "server_error" else "offline"

    private val revalidationJobs = mutableMapOf<Int, Job>()

    private fun updateRow(rowId: Int, newRow: WebImportRouteRow) {
        setState { copy(rows = rows.map { if (it.id == rowId) newRow else it }) }
        persistDraft()
        scheduleRevalidation(newRow)
    }

    /** A corrected address (status back to Pending) is re-validated by HERE shortly after the last
     * keystroke — status AND coordinates are refreshed, nothing waits for the final creation. */
    private fun scheduleRevalidation(row: WebImportRouteRow) {
        revalidationJobs.remove(row.id)?.cancel()
        val address = row.address?.trim().orEmpty()
        if (row.geocodeStatus != GeocodeStatus.Pending || address.isBlank()) return
        revalidationJobs[row.id] = scope.launch(revalidationDispatcher) {
            delay(REVALIDATION_DEBOUNCE_MS)
            val validation = try {
                validateAddress(address)
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                return@launch
            }
            setState {
                copy(
                    rows = rows.map {
                        if (it.id == row.id && it.address?.trim() == address) {
                            it.copy(
                                geocodeStatus = validation.geocodeStatus,
                                geocodeFailureReason = validation.geocodeFailureReason,
                                resolvedAddress = validation.resolvedAddress,
                                latitude = validation.latitude,
                                longitude = validation.longitude,
                            )
                        } else {
                            it
                        }
                    },
                )
            }
            persistDraft()
        }
    }

    private fun toggleDriver(driverId: String) {
        setState {
            val updated = if (driverId in selectedDriverIds) selectedDriverIds - driverId else selectedDriverIds + driverId
            copy(selectedDriverIds = updated, submitError = null)
        }
        persistDraft()
    }

    /**
     * Uploads the file as an asynchronous import (`POST /import-jobs/pdf` for `.pdf`,
     * `/import-jobs/spreadsheet` otherwise): always for a new route (`routeId` null, Écran 2 creates
     * the routes). A finished import kept by an error screen is acknowledged first — one import at
     * a time per account. A 409 (another import already active) shows that import instead.
     */
    private fun importFile(fileBytes: ByteArray, fileName: String) {
        // Retour 26/10 (R9) — a list is already there: the file's deliveries are added to it.
        if (currentState.rows.isNotEmpty() && currentState.phase.showsList()) {
            appendFile(fileBytes, fileName)
            return
        }
        if (currentState.phase is WebImportRoutePhase.TooManyStops) {
            abandonableJobId = abandonableJobId ?: shownJobId
        }
        // MIL-98 — R4: at 0 a PDF is refused before any call, nothing is sent nor counted. A
        // spreadsheet is always accepted (its columns may be recognised without the AI).
        if (fileName.isPdf() && aiCreditStore?.credit?.value?.isExhausted == true) {
            setState { WebImportRouteState(phase = WebImportRoutePhase.AiCreditExhausted(isSpreadsheet = false), fileName = fileName, routarEnabled = routarEnabled) }
            return
        }
        shownJobId = null
        pendingFile = fileBytes to fileName
        followedJobId = null
        // routarEnabled is app-level config, not tied to this import session — preserved across
        // the otherwise full state reset below (re-import/retry).
        setState { WebImportRouteState(phase = WebImportRoutePhase.Analysing, fileName = fileName, routarEnabled = routarEnabled) }
        scope.launch {
            abandonFinishedJob()
            val flow = if (fileName.isPdf()) ImportJobFlow.PDF else ImportJobFlow.SPREADSHEET
            try {
                val job = importJobTracker.submit(flow, fileBytes, fileName, routeId = null)
                pendingFile = null
                followedJobId = job.id
                onActiveJob(job)
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                handleImportFailure(e)
            }
        }
    }

    private fun WebImportRoutePhase.showsList(): Boolean =
        this == WebImportRoutePhase.VerifyAssign || this == WebImportRoutePhase.AddFile || this == WebImportRoutePhase.BatchResult

    /**
     * R9 / Q8 — a file added to the list: back on the table with "Analyse de … en cours…", the rows
     * are added once the import is ready (same consolidation as the first import), nothing is added
     * if it fails. One import at a time per account: the import of the rows already shown is
     * acknowledged first (its deliveries are in the list and in the saved draft).
     */
    private fun appendFile(fileBytes: ByteArray, fileName: String) {
        if (currentState.addedFileName != null) return
        if (fileName.isPdf() && aiCreditStore?.credit?.value?.isExhausted == true) {
            setState {
                copy(
                    phase = WebImportRoutePhase.VerifyAssign,
                    addedFileFailure = AddedFileFailure(fileName, AddedFileFailureReason.AiCreditExhausted(isSpreadsheet = false)),
                )
            }
            return
        }
        setState { copy(phase = WebImportRoutePhase.VerifyAssign, addedFileName = fileName, addedFileFailure = null) }
        scope.launch {
            shownJobId?.let { id ->
                shownJobId = null
                try {
                    importJobTracker.acknowledge(id)
                } catch (e: CancellationException) {
                    throw e
                } catch (e: Exception) {
                    importJobTracker.markConsumed(id)
                }
            }
            val flow = if (fileName.isPdf()) ImportJobFlow.PDF else ImportJobFlow.SPREADSHEET
            try {
                val job = importJobTracker.submit(flow, fileBytes, fileName, routeId = null)
                appendJobId = job.id
                onAppendJob(job)
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                if (e is AiCreditExhaustedException) refreshAiCredit()
                setState { copy(addedFileName = null, addedFileFailure = AddedFileFailure(fileName, uploadFailureReason(e, fileName))) }
            }
        }
    }

    private fun onAppendJob(job: ImportJob) {
        when {
            job.status.isInProgress -> setState { copy(addedFileName = job.fileName) }
            job.status.hasResult -> {
                appendJobId = null
                refreshAiCredit()
                val firstId = nextRowId()
                val added = when (val result = job.result) {
                    is ImportJobResult.Pdf -> result.value.deliveries.mapIndexed { i, d -> d.toRouteRow(firstId + i) }
                    is ImportJobResult.Spreadsheet -> result.value.deliveries.mapIndexed { i, r -> r.toRouteRow(firstId + i) }
                    else -> emptyList()
                }
                val merged = consolidateWebImportRouteRows(currentState.rows + added)
                if (added.isEmpty() || merged.size > MAX_STOPS) {
                    val reason = if (added.isEmpty()) {
                        AddedFileFailureReason.Document(ImportJobFailureKind.NO_DELIVERIES_FOUND)
                    } else {
                        AddedFileFailureReason.TooManyStops
                    }
                    setState { copy(addedFileName = null, addedFileFailure = AddedFileFailure(job.fileName, reason)) }
                } else {
                    setState { copy(rows = merged, totalCount = merged.size, addedFileName = null) }
                    persistDraft()
                }
                // Its deliveries are now in the list (and the saved draft): never shown again.
                importJobTracker.markConsumed(job.id)
            }
            else -> {
                appendJobId = null
                refreshAiCredit()
                setState { copy(addedFileName = null, addedFileFailure = AddedFileFailure(job.fileName, jobFailureReason(job))) }
                importJobTracker.markConsumed(job.id)
            }
        }
    }

    /** Same reading as [onActiveJob] for a failed first import. */
    private fun jobFailureReason(job: ImportJob): AddedFileFailureReason = when {
        job.status == ImportJobStatus.CANCELLED -> AddedFileFailureReason.Unknown
        else -> when (val kind = job.failureKind()) {
            ImportJobFailureKind.SERVICE_UNAVAILABLE -> AddedFileFailureReason.ServiceUnavailable
            ImportJobFailureKind.AI_CREDIT_EXHAUSTED ->
                AddedFileFailureReason.AiCreditExhausted(isSpreadsheet = job.flow == ImportJobFlow.SPREADSHEET)
            else -> AddedFileFailureReason.Document(kind)
        }
    }

    /** Same reading as [handleImportFailure] for an upload refused before any import exists. */
    private fun uploadFailureReason(error: Throwable, fileName: String): AddedFileFailureReason = when {
        error is AiCreditExhaustedException ->
            AddedFileFailureReason.AiCreditExhausted(isSpreadsheet = !fileName.isPdf(), resetAt = error.resetAt)
        error is ApiException && (error.statusCode == HTTP_BAD_REQUEST || error.statusCode == HTTP_UNPROCESSABLE_ENTITY) ->
            AddedFileFailureReason.Document(ImportJobFailureKind.UNREADABLE)
        error is ApiException && error.message == ImportJobErrorCodes.ALREADY_IN_PROGRESS -> AddedFileFailureReason.Unknown
        error is ApiException -> AddedFileFailureReason.ServiceUnavailable
        else -> AddedFileFailureReason.Unknown
    }

    /** "Réessayer": relaunches the failed import server-side from its stored file (one attempt),
     * or re-sends the local file when it never reached the server. Never automatic. */
    private fun retry() {
        val jobId = abandonableJobId
        val job = importJobTracker.state.value.current?.takeIf { it.id == jobId }
        if (job == null) {
            pendingFile?.let { (bytes, name) -> importFile(bytes, name) }
            return
        }
        // Relaunch: the next poll brings the real step back (R5).
        setState { copy(phase = WebImportRoutePhase.Analysing, analysisProgress = null) }
        scope.launch {
            try {
                importJobTracker.retry(job)
                abandonableJobId = null
            } catch (e: CancellationException) {
                throw e
            } catch (e: ApiException) {
                setState {
                    copy(
                        phase = when (e.message) {
                            AiCreditExhaustedException.CODE -> WebImportRoutePhase.AiCreditExhausted(
                                isSpreadsheet = job.flow == ImportJobFlow.SPREADSHEET,
                                resetAt = e.aiCreditResetAt,
                            )
                            ImportJobErrorCodes.SOURCE_FILE_EXPIRED -> WebImportRoutePhase.JobFailed(ImportJobFailureKind.SOURCE_FILE_EXPIRED)
                            // Other 409s re-read the import (tracker), which then shows its real state.
                            else -> WebImportRoutePhase.ServiceUnavailable(offersRetry = job.offersServerRetry)
                        },
                    )
                }
            } catch (e: Exception) {
                handleImportFailure(e)
            }
        }
    }

    /** The import of an error screen is given up when another file is picked: acknowledged so the
     * server accepts the new upload. Best effort — a failure surfaces as the upload's own 409. */
    private suspend fun abandonFinishedJob() {
        val jobId = abandonableJobId ?: return
        abandonableJobId = null
        try {
            importJobTracker.acknowledge(jobId)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            reportFailure(e)
        }
    }

    /**
     * Follows the account's active import ([ImportJobTracker.state]). Écrans 2 and 3 are never
     * replaced by the import they show; a different import (one at a time, so a newer one)
     * replaces a draft still on Écran 2.
     */
    private fun onActiveJob(job: ImportJob?) {
        if (job != null && job.id == appendJobId) {
            onAppendJob(job)
            return
        }
        when (currentState.phase) {
            WebImportRoutePhase.BatchResult -> return
            WebImportRoutePhase.VerifyAssign, WebImportRoutePhase.AddFile, is WebImportRoutePhase.TooManyStops ->
                if (job == null || job.id == shownJobId || !job.belongsToWeb()) return
            else -> Unit
        }
        // MIL-98 — a finished or failed import has spent (or not) AI credit: re-read it.
        if (job != null && !job.status.isInProgress) refreshAiCredit()
        if (job == null) {
            // Acknowledged or cancelled elsewhere: nothing left to show on Écran 1.
            if (followedJobId != null || currentState.phase == WebImportRoutePhase.ImportOnMobile) resetToIdle()
            return
        }
        when {
            job.isOriginRouteDeleted -> showJobFailure(job, WebImportRoutePhase.JobFailed(ImportJobFailureKind.ORIGIN_ROUTE_DELETED))
            job.status == ImportJobStatus.CANCELLED -> if (followedJobId == job.id) resetToIdle()
            !job.belongsToWeb() -> {
                followedJobId = null
                setState { WebImportRouteState(phase = WebImportRoutePhase.ImportOnMobile, fileName = job.fileName, routarEnabled = routarEnabled) }
            }
            job.status.isInProgress -> {
                followedJobId = job.id
                abandonableJobId = null
                setState {
                    copy(
                        phase = WebImportRoutePhase.Analysing,
                        fileName = job.fileName,
                        analysisStartedAtEpochMillis = job.startedAtEpochMillis,
                        analysisProgress = job.realProgress,
                    )
                }
            }
            job.status.hasResult -> showJobResult(job)
            else -> showJobFailure(
                job,
                when (val kind = job.failureKind()) {
                    ImportJobFailureKind.SERVICE_UNAVAILABLE -> WebImportRoutePhase.ServiceUnavailable(offersRetry = job.offersServerRetry)
                    ImportJobFailureKind.UNREADABLE -> WebImportRoutePhase.ExtractionFailed
                    ImportJobFailureKind.AI_CREDIT_EXHAUSTED -> WebImportRoutePhase.AiCreditExhausted(isSpreadsheet = job.flow == ImportJobFlow.SPREADSHEET)
                    else -> WebImportRoutePhase.JobFailed(kind)
                },
            )
        }
    }

    private fun String.isPdf(): Boolean = endsWith(".pdf", ignoreCase = true)

    /** Launched from this dashboard's flow: PDF or spreadsheet for a new route. A photo scan or an
     * import for an existing route belongs to the mobile app (its deliveries go to that route). */
    private fun ImportJob.belongsToWeb(): Boolean = routeId == null && flow != ImportJobFlow.OCR_DELIVERY_LIST

    private fun showJobFailure(job: ImportJob, phase: WebImportRoutePhase) {
        followedJobId = job.id
        abandonableJobId = job.id
        setState { WebImportRouteState(phase = phase, fileName = job.fileName, routarEnabled = routarEnabled) }
    }

    /** Ready: straight to Écran 2 — acknowledged only once its routes are created ([submit]) or the
     * import is discarded, never by just showing it. */
    private fun showJobResult(job: ImportJob) {
        followedJobId = null
        abandonableJobId = null
        shownJobId = job.id
        setState { WebImportRouteState(fileName = job.fileName, routarEnabled = routarEnabled) }
        when (val result = job.result) {
            is ImportJobResult.Pdf -> onPdfImportSucceeded(result.value)
            is ImportJobResult.Spreadsheet -> onImportSucceeded(result.value)
            // Not produced by this flow (belongsToWeb), kept for the exhaustive `when`.
            is ImportJobResult.OcrDeliveryList, null -> setState { copy(phase = WebImportRoutePhase.ServiceUnavailable(offersRetry = false)) }
        }
        // More than MAX_STOPS: unusable, given up when another file is picked.
        if (currentState.phase is WebImportRoutePhase.TooManyStops) abandonableJobId = job.id
    }

    private fun resetToIdle() {
        followedJobId = null
        abandonableJobId = null
        setState { WebImportRouteState(routarEnabled = routarEnabled) }
    }

    /** [MAX_STOPS] is checked here — right after import — rather than only at [submit] time, so an
     * oversized file is rejected before the manager spends time reviewing/correcting rows. */
    private fun onImportSucceeded(result: SpreadsheetImportResult) {
        val rows = consolidateWebImportRouteRows(result.deliveries.mapIndexed { index, row -> row.toRouteRow(index) })
        if (rows.size > MAX_STOPS) {
            setState { copy(phase = WebImportRoutePhase.TooManyStops(rows.size)) }
            return
        }
        setState {
            copy(
                phase = WebImportRoutePhase.VerifyAssign,
                // Post-consolidation count — matches what's actually shown/created, not the raw
                // pre-merge extraction count (would read as a mismatch: "3 arrêts détectés" next to
                // a table showing only 1 merged row).
                totalCount = rows.size,
                isStructuredFormat = result.isStructuredFormat,
                rows = rows,
            )
        }
        persistDraft()
        loadDrivers()
        loadLoadingAddresses()
    }

    /** Mirrors [onImportSucceeded] for the PDF path — [ImportPdfResult] has no `incompleteCount`/
     * `isStructuredFormat` of its own (unlike [SpreadsheetImportResult]): incompleteness is
     * recomputed off [WebImportRouteRow.isIncomplete] (same rule already used by the mobile PDF
     * validation screen, `ExtractedDeliveryRowUi.isComplete`), and PDF rows never trigger the
     * "free format" banner — that's a spreadsheet-only ambiguous-column concept. */
    private fun onPdfImportSucceeded(result: ImportPdfResult) {
        val rows = consolidateWebImportRouteRows(result.deliveries.mapIndexed { index, delivery -> delivery.toRouteRow(index) })
        if (rows.size > MAX_STOPS) {
            setState { copy(phase = WebImportRoutePhase.TooManyStops(rows.size)) }
            return
        }
        setState {
            copy(
                phase = WebImportRoutePhase.VerifyAssign,
                totalCount = rows.size,
                isStructuredFormat = true,
                rows = rows,
            )
        }
        persistDraft()
        loadDrivers()
        loadLoadingAddresses()
    }

    /** Upload (or relaunch) refused before any import exists — a 409 means another import is already
     * active: the tracker re-reads it and [onActiveJob] shows it. */
    private fun handleImportFailure(error: Throwable) {
        if (error is AiCreditExhaustedException) {
            refreshAiCredit()
            setState { copy(phase = WebImportRoutePhase.AiCreditExhausted(isSpreadsheet = !fileName.isPdf(), resetAt = error.resetAt)) }
            return
        }
        if (error is ApiException && error.message == ImportJobErrorCodes.ALREADY_IN_PROGRESS) {
            importJobTracker.state.value.current?.let { onActiveJob(it) } ?: resetToIdle()
            return
        }
        if (error !is ApiException && !error.isConnectivityFailure()) reportFailure(error)
        setState {
            copy(
                phase = when {
                    error is ApiException && error.statusCode == HTTP_BAD_REQUEST -> WebImportRoutePhase.InvalidFile
                    error is ApiException && error.statusCode == HTTP_UNPROCESSABLE_ENTITY -> WebImportRoutePhase.ExtractionFailed
                    error is ApiException -> WebImportRoutePhase.ServiceUnavailable()
                    error.isConnectivityFailure() -> WebImportRoutePhase.NetworkUnavailable
                    else -> WebImportRoutePhase.ServiceUnavailable()
                },
            )
        }
    }

    /** Mirrors `CreateRouteViewModel.load()` — the enterprise dashboard has exactly one
     * enterprise membership for the connected account, so the first one is used. A failure here is
     * silent (empty driver list, no dedicated error banner in the mockup) rather than blocking the
     * whole Écran 2 — the manager can still review/correct rows while retrying isn't offered. */
    private fun loadDrivers() {
        scope.launch {
            setState { copy(isLoadingDrivers = true) }
            val enterpriseId = getMyTeam().getOrNull()?.firstOrNull()?.enterpriseId
            if (enterpriseId == null) {
                setState { copy(isLoadingDrivers = false) }
                return@launch
            }
            getEnterpriseDrivers(enterpriseId).fold(
                onSuccess = { drivers -> setState { copy(isLoadingDrivers = false, drivers = drivers) } },
                onFailure = { setState { copy(isLoadingDrivers = false) } },
            )
        }
    }

    /** Écran 2 → Écran 3 — always `POST /route-batches` (MIL-39), never the single-route endpoint,
     * regardless of [WebImportRouteState.selectedDriverIds]' size. A 503 with [optimize] `true` means
     * RoutaR itself is down: the manager stays on Écran 2, sees `optimization_unavailable`, and can
     * either retry the same optimized call once RoutaR is back, or fire
     * [WebImportRouteIntent.SubmitWithoutOptimization] ([optimize] `false`) to send the round-robin
     * split immediately instead of waiting. */
    private fun submit(optimize: Boolean) {
        if (currentState.isSubmitting) return
        if (currentState.selectedDriverIds.isEmpty()) {
            setState { copy(submitError = "driver_required") }
            return
        }
        // MIL-99 — the CTA is already disabled without a loading address, this is the guard behind it.
        val loadingAddressId = currentState.selectedLoadingAddressId
        if (loadingAddressId == null) {
            setState { copy(submitError = "loading_address_required") }
            return
        }
        // Retour 26/10 (R2) — same: the button is greyed while an address is to correct.
        if (currentState.toFixCount > 0) return
        // Guard set synchronously, before scope.launch (Dispatchers.Default dispatches
        // asynchronously) — closes the double-tap window that let two batch-create calls fire.
        setState { copy(isSubmitting = true, submitError = null) }
        scope.launch {
            // Ad hoc feature (2026-08) — RoutaR kill switch: when known off ahead of time, force the
            // round-robin split outright rather than attempting a call that would fail every time —
            // equivalent to the manager firing SubmitWithoutOptimization themselves.
            val effectiveOptimize = optimize && currentState.routarEnabled
            val drafts = currentState.rows.map { it.toDraft() }
            val fingerprint = RouteBatchFingerprint(drafts, currentState.selectedDriverIds, loadingAddressId, date = "", optimize = effectiveOptimize)
            // R7 — nothing changed since the split was computed: shown again, no RoutaR call.
            val kept = currentState.batchResult
            if (kept != null && currentState.batchFingerprint == fingerprint) {
                setState { copy(isSubmitting = false, phase = WebImportRoutePhase.BatchResult) }
                return@launch
            }
            // R8 — the split not sent yet is abandoned before a new one (404 = already gone).
            val abandoned = kept?.batchId ?: currentState.staleBatchId
            if (abandoned != null) {
                try {
                    deleteRouteBatch?.invoke(abandoned)
                } catch (e: CancellationException) {
                    throw e
                } catch (e: Exception) {
                    setState { copy(isSubmitting = false, submitError = mapBatchError(e)) }
                    return@launch
                }
                setState { copy(batchResult = null, batchFingerprint = null, staleBatchId = null, batchStopsByRouteId = emptyMap()) }
                persistDraft()
            }
            runCatching {
                createRouteBatch(
                    currentState.selectedDriverIds.toList(),
                    drafts,
                    optimize = effectiveOptimize,
                    loadingAddressId = loadingAddressId,
                )
            }.fold(
                onSuccess = { result ->
                    setState { copy(isSubmitting = false, phase = WebImportRoutePhase.BatchResult, batchResult = result, batchFingerprint = fingerprint) }
                    persistDraft()
                    // A quota at 0 doesn't fail the batch (R6, R11): 201 with optimizationSkippedReason.
                    if (effectiveOptimize) refreshQuota()
                    // The deliveries now exist in the created routes: the import is consumed
                    // (anti-duplicate — remembered on disk and acknowledged until it succeeds).
                    shownJobId?.let { importJobTracker.markConsumed(it) }
                    loadBatchStops(result)
                },
                onFailure = { e ->
                    if (e is AddressesToFixException) showAddressesToFix(e)
                    else setState { copy(isSubmitting = false, submitError = mapBatchError(e)) }
                },
            )
        }
    }

    /**
     * 422 `error.route.addresses_to_fix` — the refused rows (same positions as the drafts sent) go red
     * with their motif under "À corriger". Without the list (older back): a generic "réessayez",
     * never "à corriger" over an empty list.
     */
    private fun showAddressesToFix(e: AddressesToFixException) {
        val marked = currentState.rows.markAddressesToFix(e.deliveries) { reason ->
            copy(geocodeStatus = GeocodeStatus.Failed, geocodeFailureReason = reason)
        }
        if (marked == null) {
            setState { copy(isSubmitting = false, submitError = "addresses_to_fix_retry") }
            return
        }
        setState { copy(isSubmitting = false, rows = marked, filter = DeliveryFilter.ToFix, submitError = "addresses_to_fix") }
        persistDraft()
    }

    /** `RouteBatchResultDto` carries no per-stop detail (name/address), only counts (MIL-39) — one
     * `GET /routes/{id}` (MIL-38, [getRouteDetail]) per driver's freshly created route fills in the
     * "first 4 stops" preview on Écran 3. A per-route failure here yields an empty stop list for
     * that one driver rather than failing the whole screen — the counts/distance/duration on its
     * card (from [WebImportRouteState.batchResult] itself) stay correct either way — but the failed
     * route id is tracked in [WebImportRouteState.batchStopsFailedRouteIds] so the card can show a
     * "couldn't load" message instead of an indistinguishable empty preview. */
    private fun loadBatchStops(result: RouteBatchResult) {
        scope.launch {
            setState { copy(isLoadingBatchStops = true) }
            val stopsByRouteId = mutableMapOf<String, List<Delivery>>()
            val failedRouteIds = mutableSetOf<String>()
            result.drivers.forEach { proposal ->
                val routeDetail = runCatching { getRouteDetail(proposal.routeId) }.getOrNull()
                if (routeDetail == null) failedRouteIds += proposal.routeId
                stopsByRouteId[proposal.routeId] = routeDetail?.deliveries?.sortedBy { it.sortOrder }.orEmpty()
            }
            setState {
                copy(
                    isLoadingBatchStops = false,
                    batchStopsByRouteId = stopsByRouteId,
                    batchStopsFailedRouteIds = failedRouteIds,
                )
            }
        }
    }

    /** Écran 3 — "Valider et envoyer les tournées": `POST /route-batches/{id}/dispatch` (MIL-39),
     * scheduling every route of the batch in one action. No re-optimization intent from this step
     * (out of MIL-51's scope) — a failure here leaves [WebImportRouteState.batchResult] untouched so
     * the manager can simply retry. */
    private fun validateAndSend() {
        if (currentState.isDispatchingBatch) return
        val batchId = currentState.batchResult?.batchId ?: return
        // Guard set synchronously, before scope.launch (Dispatchers.Default dispatches
        // asynchronously) — closes the double-tap window that let two dispatch calls fire.
        setState { copy(isDispatchingBatch = true, submitError = null) }
        scope.launch {
            runCatching { dispatchRouteBatch(batchId) }.fold(
                onSuccess = {
                    localCacheStore.remove(DRAFT_STORAGE_KEY)
                    setState { copy(isDispatchingBatch = false) }
                    _navEvent.send(WebImportRouteNavEvent.Dispatched)
                },
                onFailure = { e -> setState { copy(isDispatchingBatch = false, submitError = mapBatchError(e)) } },
            )
        }
    }

    private fun mapBatchError(e: Throwable): String = when {
        // MIL-97 — R11: never the generic 403 message for the quota.
        e.isOptimizationQuotaExceeded -> "optimization_quota_exceeded"
        // MIL-99 — 400 `error.loading_address_required`, checked before the generic 400 below.
        e is ApiException && e.message.endsWith("loading_address_required") -> "loading_address_required"
        // Retour 26/10 — R2 checked again server-side (422). The rows to mark are handled by
        // showAddressesToFix; reaching here means no list to show.
        e is ApiException && e.message == AddressesToFixException.CODE -> "addresses_to_fix_retry"
        e is ApiException && e.statusCode == HTTP_CONFLICT -> "already_dispatched"
        e is ApiException && e.statusCode == HTTP_NOT_FOUND -> "route_not_found"
        e is ApiException && e.statusCode == HTTP_BAD_REQUEST -> "validation_error"
        e is ApiException && e.statusCode == HTTP_SERVICE_UNAVAILABLE -> "optimization_unavailable"
        e is ApiException -> "server_error"
        else -> "offline"
    }
}
